import { useReactiveVar } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import { NativeWindStyleSheet } from "nativewind";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Platform, StyleProp, View, ViewStyle } from "react-native";
import { Snackbar, Text } from "react-native-paper";
import screens from "../../theme/screens";
import { errorsVar, GraphQLErrorsVar } from "../components/makeVars";
import tabBarVisibleVar from "../screens/MainScreen/tabBarVisibleVar";

type SnackBarValue = (SnackBarData: SnackBarData) => void;
interface Action {
  label: string;
  onPress: () => void;
}
interface SnackBarData {
  message?: React.ReactNode;
  duration?: number;
  showSnack?: boolean;
  action?: Action;
  snackKey?: number;
  style?: StyleProp<ViewStyle>;
  onClose?(): void;
  hideClose?: boolean;
  icon?: keyof typeof Feather.glyphMap;
}

const SnackBarContext = createContext<SnackBarValue>(() => {});

function graphqlErrorToMessage({
  networkError,
  graphQLErrors,
}: GraphQLErrorsVar): Pick<SnackBarData, "icon" | "message" | "hideClose"> {
  const serverError = graphQLErrors ? graphQLErrors[0] : undefined;
  let serverMessage = serverError?.message || "Server Error";
  if (serverMessage.includes("was omitted due to permissions")) {
    serverMessage = "Access Denied";
  }
  return {
    hideClose: true,
    ...(networkError ? { icon: "wifi-off", message: "Something went wrong." } : { icon: "cloud-off", message: serverMessage }),
  };
}

NativeWindStyleSheet.create({
  styles: {
    "snack-position": {
      position: "relative",
    },
    "snack-position@0": {
      // @ts-ignore position fixed is not valid for RN
      position: "fixed",
    },
  },
  atRules: {
    "snack-position": [[["media", `(min-width: ${screens.lg}px)`]]],
  },
  topics: {
    "snack-position": ["width"],
  },
});

export function SnackBarProvider(props: { children: ReactNode }) {
  const [snackBar, setSnackBar] = useState<SnackBarData>({ showSnack: false });

  const graphqlErrors = useReactiveVar(errorsVar);
  const tabBarVisible = useReactiveVar(tabBarVisibleVar);

  const showSnackBar = useCallback(
    (data: SnackBarData) => {
      setSnackBar({
        ...data,
        showSnack: true,
        snackKey: new Date().getTime(),
      });
    },
    [setSnackBar]
  );

  useEffect(() => {
    if (!graphqlErrors) {
      hideSnackBar();
      return;
    }
    showSnackBar({
      ...graphqlErrorToMessage(graphqlErrors),
      duration: 60000,
      action: {
        label: "Retry",
        onPress() {
          graphqlErrors.retry();
        },
      },
    });
  }, [graphqlErrors]);

  const hideSnackBar = () => setSnackBar({ showSnack: false });

  const { message, duration, showSnack, action, style, snackKey, onClose, icon, hideClose } = snackBar;

  const iconProps = hideClose ? {} : { icon: "x", onIconPress: hideSnackBar };

  return (
    <>
      <SnackBarContext.Provider value={showSnackBar} {...props} />
      <Snackbar
        key={snackKey}
        duration={duration || 2000}
        visible={!!showSnack && !!message}
        action={action}
        className={`rounded-lg ${Platform.select({
          web: "lg:top-20 lg:right-4 snack-position",
        })}`}
        style={style}
        wrapperStyle={tabBarVisible ? { marginBottom: 50 } : undefined}
        onDismiss={() => {
          setSnackBar({ showSnack: false });
          if (onClose) onClose();
        }}
        {...iconProps}
      >
        <View className="flex-row items-center justify-between w-full web:lg:pr-10">
          <View className="flex-row">
            {icon && <Feather name={icon} color="white" size={20} />}
            <Text className="text-regular-tight font-400 text-sky-white ml-3 text-wrap px-1">{message}</Text>
          </View>
        </View>
      </Snackbar>
    </>
  );
}

const useSnackBar = () => useContext(SnackBarContext);

export default useSnackBar;
